/**
 * @fileoverview NacebelCodeInput Component
 *
 * A tag/chip input for managing an array of NACEBEL codes
 * (Belgian industry classification codes).
 */

import React, { useState, KeyboardEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';

interface NacebelCodeInputProps {
  codes: string[];
  onChange: (codes: string[]) => void;
  isDisabled?: boolean;
}

const NacebelCodeInput: React.FC<NacebelCodeInputProps> = ({
  codes,
  onChange,
  isDisabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (codes.includes(trimmed)) {
      setInputValue('');
      return;
    }

    onChange([...codes, trimmed]);
    setInputValue('');
  };

  const handleRemove = (codeToRemove: string) => {
    onChange(codes.filter((code) => code !== codeToRemove));
  };

  return (
    <div className="flex flex-col gap-2">
      {codes.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {codes.map((code) => (
            <Badge key={code} variant="secondary" className="gap-1 pr-1">
              {code}
              {!isDisabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(code)}
                  className="ml-0.5 rounded-sm opacity-70 transition-opacity hover:opacity-100"
                  aria-label={`Verwijder ${code}`}
                >
                  <X size={12} />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      {!isDisabled && (
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Code toevoegen en Enter drukken..."
          disabled={isDisabled}
        />
      )}

      {codes.length === 0 && isDisabled && (
        <p className="text-sm text-muted-foreground">Geen codes opgegeven</p>
      )}
    </div>
  );
};

export default NacebelCodeInput;
